import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Download, Pencil, Plus, Upload } from "lucide-react";

type FormularioProduto = {
  id?: number;
  cod: string;
  ncm: string;
  descricao: string;
  venda: string;
  emitenteId: string;
};

const formularioVazio: FormularioProduto = { cod: "", ncm: "", descricao: "", venda: "", emitenteId: "" };
const colunasPlanilha = ["CÓDIGO", "NCM", "DESCRIÇÃO", "VENDA", "EMITENTE ID"];

function moeda(valor: unknown) {
  return Number(valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function valorDaPlanilha(valor: unknown) {
  if (typeof valor === "number") return valor;
  const texto = String(valor ?? "").trim();
  if (!texto) return "";
  return texto.includes(",") ? Number(texto.replace(/\./g, "").replace(",", ".")) : Number(texto);
}

export default function CadastroBasicoProduto() {
  const utils = trpc.useUtils();
  const arquivoRef = useRef<HTMLInputElement>(null);
  const [emitenteSelecionado, setEmitenteSelecionado] = useState("");
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(false);
  const [formulario, setFormulario] = useState<FormularioProduto>(formularioVazio);
  const [errosImportacao, setErrosImportacao] = useState<string[]>([]);
  const [fotoPortalUrl, setFotoPortalUrl] = useState("");
  const [catalogoPublico, setCatalogoPublico] = useState(false);
  const [portalCliente, setPortalCliente] = useState(false);

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const produtos = trpc.produtosBasicos.list.useQuery(
    { emitenteId: Number(emitenteSelecionado) },
    { enabled: !!emitenteSelecionado }
  );
  const configPortal = trpc.portalConfig.produto.get.useQuery(
    { produtoId: formulario.id || 0 },
    { enabled: modalAberto && editando && !!formulario.id }
  );
  const salvarConfigPortal = trpc.portalConfig.produto.save.useMutation({
    onError: (erro) => toast.error(erro.message),
  });

  useEffect(() => {
    if (!configPortal.data) return;
    setFotoPortalUrl(configPortal.data.fotoPortalUrl || "");
    setCatalogoPublico(Boolean(configPortal.data.catalogoPublico));
    setPortalCliente(Boolean(configPortal.data.portalCliente));
  }, [configPortal.data]);
  const criar = trpc.produtosBasicos.create.useMutation({
    onSuccess: async () => {
      await utils.produtosBasicos.list.invalidate();
      toast.success("Produto cadastrado.");
      setModalAberto(false);
    },
    onError: (erro) => toast.error(erro.message),
  });
  const atualizar = trpc.produtosBasicos.update.useMutation({
    onSuccess: async () => {
      await utils.produtosBasicos.list.invalidate();
      toast.success("Produto atualizado.");
      setModalAberto(false);
    },
    onError: (erro) => toast.error(erro.message),
  });
  const importar = trpc.produtosBasicos.importar.useMutation({
    onError: (erro) => toast.error(erro.message),
  });

  const produtosOrdenados = useMemo(
    () => [...(produtos.data ?? [])].sort((a: any, b: any) => String(a.cod).localeCompare(String(b.cod), "pt-BR", { numeric: true })),
    [produtos.data]
  );

  function abrirCriacao() {
    if (!emitenteSelecionado) {
      toast.error("Selecione o emitente antes de cadastrar.");
      return;
    }
    setEditando(false);
    setFormulario({ ...formularioVazio, emitenteId: emitenteSelecionado });
    setFotoPortalUrl("");
    setCatalogoPublico(false);
    setPortalCliente(false);
    setModalAberto(true);
  }

  function abrirEdicao(produto: any) {
    setEditando(true);
    setFormulario({
      id: produto.id,
      cod: produto.cod || "",
      ncm: produto.ncm || "",
      descricao: produto.descricao || "",
      venda: String(produto.venda ?? ""),
      emitenteId: String(produto.emitenteId),
    });
    setFotoPortalUrl("");
    setCatalogoPublico(false);
    setPortalCliente(false);
    setModalAberto(true);
  }

  function validarFormulario() {
    const cod = formulario.cod.trim();
    if (!cod) return "Código é obrigatório.";
    if (cod.startsWith("0")) return "Código não pode iniciar com zero.";
    if (!editando && !formulario.ncm.trim()) return "NCM é obrigatório.";
    if (!formulario.descricao.trim()) return "Descrição é obrigatória.";
    if (!formulario.venda.trim() || !Number.isFinite(Number(formulario.venda))) return "Venda é obrigatória.";
    if (!formulario.emitenteId) return "Emitente é obrigatório.";
    return null;
  }

  async function salvar(event: React.FormEvent) {
    event.preventDefault();
    const erro = validarFormulario();
    if (erro) {
      toast.error(erro);
      return;
    }
    const dados = {
      cod: formulario.cod.trim(),
      descricao: formulario.descricao.trim(),
      venda: Number(formulario.venda),
      emitenteId: Number(formulario.emitenteId),
    };
    try {
      const produtoId = editando && formulario.id
        ? (await atualizar.mutateAsync({ id: formulario.id, ...dados }), formulario.id)
        : (await criar.mutateAsync({ ...dados, ncm: formulario.ncm.trim() })).id;
      await salvarConfigPortal.mutateAsync({ produtoId, fotoPortalUrl: fotoPortalUrl || null, catalogoPublico, portalCliente });
    } catch {
      // As mutações já exibem o erro específico.
    }
  }

  async function enviarFotoPortal(file: File | undefined) {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      toast.error(data.error || "Não foi possível enviar a foto.");
      return;
    }
    setFotoPortalUrl(data.url || "");
  }

  function exportarExcel() {
    if (!emitenteSelecionado) {
      toast.error("Selecione o emitente antes de exportar.");
      return;
    }
    const linhas = produtosOrdenados.map((produto: any) => ({
      "CÓDIGO": produto.cod,
      "NCM": produto.ncm,
      "DESCRIÇÃO": produto.descricao,
      "VENDA": Number(produto.venda || 0),
      "EMITENTE ID": produto.emitenteId,
    }));
    const planilha = XLSX.utils.json_to_sheet(linhas, { header: colunasPlanilha });
    planilha["!cols"] = [{ wch: 18 }, { wch: 16 }, { wch: 48 }, { wch: 16 }, { wch: 16 }];
    const arquivo = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(arquivo, planilha, "Produtos");
    XLSX.writeFile(arquivo, `cadastro_basico_produtos_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  function baixarTemplate() {
    const planilha = XLSX.utils.aoa_to_sheet([colunasPlanilha]);
    planilha["!cols"] = [{ wch: 18 }, { wch: 16 }, { wch: 48 }, { wch: 16 }, { wch: 16 }];
    const arquivo = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(arquivo, planilha, "Produtos");
    XLSX.writeFile(arquivo, "template_cadastro_basico_produtos.xlsx");
  }

  async function importarArquivo(event: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = event.target.files?.[0];
    if (!arquivo) return;
    setErrosImportacao([]);
    try {
      const buffer = await arquivo.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const linhas = XLSX.utils.sheet_to_json<any>(sheet, { defval: "" }).map((linha) => ({
        cod: linha["CÓDIGO"],
        ncm: linha["NCM"],
        descricao: linha["DESCRIÇÃO"],
        venda: valorDaPlanilha(linha["VENDA"]),
        emitenteId: linha["EMITENTE ID"],
      }));
      if (!linhas.length) {
        toast.error("A planilha não possui linhas para importar.");
        return;
      }
      const resultado = await importar.mutateAsync({ produtos: linhas });
      if (resultado.erros.length) {
        setErrosImportacao(resultado.erros);
        toast.error("A importação foi recusada. Corrija as linhas listadas.");
        return;
      }
      await utils.produtosBasicos.list.invalidate();
      toast.success(`${resultado.criados} produto(s) cadastrado(s).`);
    } catch (erro: any) {
      toast.error(erro.message || "Não foi possível importar a planilha.");
    } finally {
      if (arquivoRef.current) arquivoRef.current.value = "";
    }
  }

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Cadastro Básico de Produto</h1>
          <p className="text-sm text-muted-foreground">Código, NCM, descrição e venda</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportarExcel} disabled={!emitenteSelecionado}><Download className="mr-2 h-4 w-4" />Exportar Excel</Button>
          <Button variant="outline" onClick={baixarTemplate}><Download className="mr-2 h-4 w-4" />Baixar template</Button>
          <Button variant="outline" onClick={() => arquivoRef.current?.click()} disabled={importar.isPending}><Upload className="mr-2 h-4 w-4" />{importar.isPending ? "Importando..." : "Importar Excel"}</Button>
          <input ref={arquivoRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={importarArquivo} />
          <Button onClick={abrirCriacao}><Plus className="mr-2 h-4 w-4" />Novo produto</Button>
        </div>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="max-w-md">
            <Label>Emitente</Label>
            <Select value={emitenteSelecionado} onValueChange={setEmitenteSelecionado}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
              <SelectContent>{emitentes.map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.nomeFantasia || emitente.razaoSocial}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {errosImportacao.length > 0 && <Card className="border-destructive"><CardHeader><CardTitle className="text-base text-destructive">Erros da importação</CardTitle></CardHeader><CardContent><ul className="list-disc space-y-1 pl-5 text-sm">{errosImportacao.map((erro, indice) => <li key={`${erro}-${indice}`}>{erro}</li>)}</ul></CardContent></Card>}

      <Card>
        <CardHeader><CardTitle>Produtos</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!emitenteSelecionado ? <p className="p-6 text-sm text-muted-foreground">Selecione o emitente para listar os produtos.</p> : <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b bg-muted/40"><th className="px-3 py-2 text-left">Código</th><th className="px-3 py-2 text-left">NCM</th><th className="px-3 py-2 text-left">Descrição</th><th className="px-3 py-2 text-right">Venda</th><th className="px-3 py-2 text-right">Ações</th></tr></thead><tbody>{produtosOrdenados.map((produto: any) => <tr key={produto.id} className="border-b"><td className="px-3 py-2">{produto.cod}</td><td className="px-3 py-2">{produto.ncm}</td><td className="px-3 py-2">{produto.descricao}</td><td className="px-3 py-2 text-right">{moeda(produto.venda)}</td><td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={() => abrirEdicao(produto)}><Pencil className="mr-2 h-4 w-4" />Editar</Button></td></tr>)}{!produtos.isLoading && produtosOrdenados.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Nenhum produto encontrado.</td></tr>}</tbody></table></div>}
        </CardContent>
      </Card>

      <Dialog open={modalAberto} onOpenChange={(aberto) => { setModalAberto(aberto); if (!aberto) { setFormulario(formularioVazio); setFotoPortalUrl(""); setCatalogoPublico(false); setPortalCliente(false); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editando ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          <form className="space-y-4" onSubmit={salvar}>
            <div><Label>Emitente</Label><Select value={formulario.emitenteId} onValueChange={(emitenteId) => setFormulario((atual) => ({ ...atual, emitenteId }))}><SelectTrigger className="mt-1"><SelectValue placeholder="Selecione o emitente" /></SelectTrigger><SelectContent>{emitentes.map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.nomeFantasia || emitente.razaoSocial}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Código</Label><Input className="mt-1" value={formulario.cod} onChange={(event) => setFormulario((atual) => ({ ...atual, cod: event.target.value }))} /></div>
            <div><Label>NCM</Label><Input className="mt-1" value={formulario.ncm} disabled={editando} onChange={(event) => setFormulario((atual) => ({ ...atual, ncm: event.target.value }))} /></div>
            <div><Label>Descrição</Label><Input className="mt-1" value={formulario.descricao} onChange={(event) => setFormulario((atual) => ({ ...atual, descricao: event.target.value }))} /></div>
            <div><Label>Venda</Label><Input className="mt-1" type="number" step="0.01" value={formulario.venda} onChange={(event) => setFormulario((atual) => ({ ...atual, venda: event.target.value }))} /></div>
            <fieldset className="space-y-3 rounded-lg border p-3"><legend className="px-1 text-sm font-medium">Portal do Cliente</legend><div><Label>Foto do produto</Label><Input className="mt-1" type="file" accept="image/*" onChange={(event) => enviarFotoPortal(event.target.files?.[0])} />{fotoPortalUrl && <img src={fotoPortalUrl} alt="Foto do produto" className="mt-2 h-20 w-20 rounded border object-cover" />}</div><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={catalogoPublico} onChange={(event) => setCatalogoPublico(event.target.checked)} />Catálogo público</label><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={portalCliente} onChange={(event) => setPortalCliente(event.target.checked)} />Portal do cliente</label></fieldset>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setModalAberto(false)}>Cancelar</Button><Button type="submit" disabled={criar.isPending || atualizar.isPending}>{criar.isPending || atualizar.isPending ? "Salvando..." : "Salvar"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
